import { useState } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, MoreHorizontal, Search, X } from "lucide-react";
import { fuzzyMatch } from "../../lib/searchUtils";
import { formatCurrency as formatCurrencyBase } from "../../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { SearchableSelect } from "../ui/searchable-select";
const MiscSettingsTab = ({
  miscItems = [],
  miscUnits = [],
  billingCategories = [],
  getAuthHeaders,
  onRefresh,
}) => {
  const [miscDialogOpen, setMiscDialogOpen] = useState(false);
  const [editingMiscItem, setEditingMiscItem] = useState(null);
  const [search, setSearch] = useState("");
  const [miscForm, setMiscForm] = useState({
    description: "",
    quantity: "1",
    unit: "",
    unit_price: "",
    material_cost: "",
    labor_cost: "",
    notes: "",
    billing_category: "",
    billing_category_per_job: false
  });

  const resetMiscForm = () => {
    setMiscForm({
      description: "",
      quantity: "1",
      unit: "",
      unit_price: "",
      material_cost: "",
      labor_cost: "",
      notes: "",
      billing_category: "",
      billing_category_per_job: false
    });
    setEditingMiscItem(null);
  };

  const openEditMiscItem = (item) => {
    setEditingMiscItem(item);
    setMiscForm({
      description: item.description || "",
      quantity: item.quantity?.toString() || "1",
      unit: item.unit || "",
      unit_price: item.unit_price?.toString() || "",
      material_cost: item.material_cost?.toString() || "",
      labor_cost: item.labor_cost?.toString() || "",
      notes: item.notes || "",
      billing_category: item.billing_category || "",
      billing_category_per_job: item.billing_category_per_job || false
    });
    setMiscDialogOpen(true);
  };

  const handleMiscSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        description: miscForm.description,
        quantity: parseFloat(miscForm.quantity) || 1,
        unit: miscForm.unit || "",
        unit_price: parseFloat(miscForm.unit_price) || 0,
        material_cost: parseFloat(miscForm.material_cost) || 0,
        labor_cost: parseFloat(miscForm.labor_cost) || 0,
        notes: miscForm.notes,
        billing_category: miscForm.billing_category_per_job ? null : (miscForm.billing_category || null),
        billing_category_per_job: !!miscForm.billing_category_per_job,
        id: editingMiscItem?.id || ""
      };
      
      if (editingMiscItem) {
        await apiClient.put(`/settings/misc/${editingMiscItem.id}`, payload);
        toast.success("Misc item updated");
      } else {
        await apiClient.post(`/settings/misc`, payload);
        toast.success("Misc item added");
      }
      onRefresh();
      setMiscDialogOpen(false);
      resetMiscForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDeleteMiscItem = async (id) => {
    try {
      await apiClient.delete(`/settings/misc/${id}`);
      toast.success("Misc item deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete misc item");
    }
  };

  const formatCurrency = (amount) => formatCurrencyBase(amount, true);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <MoreHorizontal className="w-5 h-5" />
            Misc Bid Items
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Predefined misc items that can be quickly added to bid sheets
          </p>
        </div>
        <Dialog open={miscDialogOpen} onOpenChange={(o) => { setMiscDialogOpen(o); if (!o) resetMiscForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-misc-settings-btn"><Plus className="w-4 h-4 mr-2" />Add Misc Item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingMiscItem ? "Edit Misc Item" : "Add New Misc Item"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMiscSubmit} className="space-y-4">
              <div className="form-group">
                <Label>Description *</Label>
                <Input 
                  value={miscForm.description} 
                  onChange={(e) => setMiscForm({...miscForm, description: e.target.value})} 
                  required 
                  placeholder="e.g., Delivery Fee, Clean Up" 
                  data-testid="misc-settings-description-input" 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Default Quantity *</Label>
                  <NumericInput 
                    decimals={2}
                    min={0}
                    value={miscForm.quantity} 
                    onChange={(e) => setMiscForm({...miscForm, quantity: e.target.value})} 
                    placeholder="1" 
                    data-testid="misc-settings-quantity-input" 
                  />
                </div>
                <div className="form-group">
                  <Label>Unit</Label>
                  {miscUnits.length > 0 ? (
                    <Select 
                      value={miscForm.unit} 
                      onValueChange={(v) => setMiscForm({...miscForm, unit: v})}
                    >
                      <SelectTrigger data-testid="misc-settings-unit-select">
                        <SelectValue placeholder="Select unit..." />
                      </SelectTrigger>
                      <SelectContent>
                        {[...miscUnits].sort((a, b) => a.localeCompare(b)).map((unit) => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input 
                      value={miscForm.unit} 
                      onChange={(e) => setMiscForm({...miscForm, unit: e.target.value})} 
                      placeholder="each, trip, load..." 
                      data-testid="misc-settings-unit-input" 
                    />
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {miscUnits.length === 0 ? "Add units in Options tab" : "Select a unit or add more in Options"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Unit Price ($)</Label>
                  <NumericInput 
                    decimals={2}
                    min={0}
                    value={miscForm.unit_price} 
                    onChange={(e) => setMiscForm({...miscForm, unit_price: e.target.value})} 
                    placeholder="0.00" 
                    data-testid="misc-settings-unit-price-input" 
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Material Cost ($)</Label>
                  <NumericInput 
                    decimals={2}
                    min={0}
                    value={miscForm.material_cost} 
                    onChange={(e) => setMiscForm({...miscForm, material_cost: e.target.value})} 
                    placeholder="0.00 (optional)" 
                    data-testid="misc-settings-material-cost-input" 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Taxable portion (for sales tax calc)</p>
                </div>
                <div className="form-group">
                  <Label>Labor Cost ($)</Label>
                  <NumericInput 
                    decimals={2}
                    min={0}
                    value={miscForm.labor_cost} 
                    onChange={(e) => setMiscForm({...miscForm, labor_cost: e.target.value})} 
                    placeholder="0.00 (optional)" 
                    data-testid="misc-settings-labor-cost-input" 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Labor portion (for burden calc)</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
                <Checkbox
                  id="misc-billing-category-per-job"
                  checked={!!miscForm.billing_category_per_job}
                  onCheckedChange={(checked) => setMiscForm({ ...miscForm, billing_category_per_job: !!checked, billing_category: checked ? "" : miscForm.billing_category })}
                  data-testid="misc-billing-category-per-job-checkbox"
                />
                <Label htmlFor="misc-billing-category-per-job" className="text-sm cursor-pointer">
                  Set billing category in bid sheet
                  <span className="block text-xs text-muted-foreground font-normal">Required per line when added to a bid</span>
                </Label>
              </div>
              {!miscForm.billing_category_per_job && (
              <div className="form-group">
                <Label>Billing Category</Label>
                <SearchableSelect
                  value={miscForm.billing_category}
                  onValueChange={(v) => setMiscForm({ ...miscForm, billing_category: v || "" })}
                  options={billingCategories.map((c) => ({ value: c, label: c }))}
                  placeholder={billingCategories.length ? "Select billing category (optional)" : "Add billing categories in Options"}
                  searchPlaceholder="Search billing categories..."
                  data-testid="misc-billing-category-select"
                />
                <p className="text-xs text-muted-foreground mt-1">Groups this item on the Schedule of Values when the first billing is created.</p>
              </div>
              )}
              <div className="form-group">
                <Label>Notes</Label>
                <Textarea 
                  value={miscForm.notes} 
                  onChange={(e) => setMiscForm({...miscForm, notes: e.target.value})} 
                  rows={2} 
                  placeholder="Optional notes..." 
                  data-testid="misc-settings-notes-input" 
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-misc-settings-btn">
                {editingMiscItem ? "Update Misc Item" : "Add Misc Item"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search misc items..." className="pl-9 pr-9" data-testid="misc-search-input" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        {miscItems.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MoreHorizontal className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No misc items added yet</p>
            <p className="text-sm mt-1">Add common bid items like delivery fees, clean up, etc.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Material</TableHead>
                <TableHead className="text-right">Labor</TableHead>
                <TableHead className="text-right w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {miscItems.filter((item) => {
                if (!search.trim()) return true;
                const searchable = [item.description, item.notes, item.unit, item.billing_category].filter(Boolean).join(" ");
                return fuzzyMatch(search, searchable);
              }).map((item) => (
                <TableRow key={item.id} className="table-row">
                  <TableCell>
                    <div>
                      <p className="font-medium">{item.description}</p>
                      {item.notes && <p className="text-sm text-muted-foreground truncate max-w-xs">{item.notes}</p>}
                      {item.billing_category && <span className="inline-flex mt-0.5 px-1.5 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`misc-billing-badge-${item.id}`}>Billing: {item.billing_category}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{item.quantity || 1}</TableCell>
                  <TableCell>{item.unit || "-"}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.unit_price)}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {item.material_cost > 0 ? formatCurrency(item.material_cost) : "-"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {item.labor_cost > 0 ? formatCurrency(item.labor_cost) : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditMiscItem(item)} data-testid={`edit-misc-settings-${item.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-misc-settings-${item.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Misc Item?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently remove &quot;{item.description}&quot;.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteMiscItem(item.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default MiscSettingsTab;
